import React, { useEffect, useRef, useState } from 'react';
import './admin-game-data.styles.scss';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import SearchBar from '@/components/SHARED/search-bar/search-bar.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { properCase } from '@/utils/properCase';
import { faClone, faMagnifyingGlass, faTrash } from '@fortawesome/free-solid-svg-icons';
import UserIcon from '@/components/layout-auth/user-icon/user-icon.component';
import { sortPlayers } from '@/utils/sortPlayers';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { useSelector } from 'react-redux';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { useNavigate } from 'react-router-dom';
import { sortElementsNoThe } from '@/utils/sortElements';

const AdminDataView = ({
  view,
  dataToDisplay
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const [filteredDataToDisplay, setFilteredDataToDisplay] = useState([]);
  const [sortButtons, setSortButtons] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [fullLoading, setFullLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const filterRef = useRef([]);
  const timeoutRef = useRef(null);
  const approveRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  }, [])

  useEffect(() => {
    if (!view || !dataToDisplay) return;
    setLoading(false);
    setSortButtons([]);
    filterRef.current = [];
    setTypeFilter(filterRef.current);
    if (view === 'elements') {
      getSortButtons();
    } else if (view === 'players') {
      setSortButtons(['players', 'teams']);
      filterRef.current = ['players'];
      setTypeFilter(['players']);
    }
    search();
    timeoutRef.current = setTimeout(() => {
      setLoading(false);
      clearTimeout(timeoutRef.current);
    }, 3000);
  }, [view, dataToDisplay])

  function getSortButtons() {
    let buttonArr = [];
    Object.values(dataToDisplay).forEach((d, index) => {
      let type = d.type;
      if (
        type === 'item' && 
        d.opt_itemType === 'prizepack' &&
        !buttonArr.includes('prizepack')) {
        buttonArr.push('prizepack');
      } else if (!buttonArr.includes(d.type)) {
        buttonArr.push(d.type);
      }
    })
    setSortButtons(buttonArr);
  }

  function filterByType(type) {
    if (view === 'elements') {
      if (!filterRef.current.includes(type)) {
        filterRef.current.push(type);
      } else {
        filterRef.current.splice(filterRef.current.indexOf(type), 1);
      }
    } else if (view === 'players') {
      filterRef.current = [type];
    }
    setTypeFilter([...filterRef.current]);
    search();
  }

  function search() {
    const searchEl = document.getElementById('item-search');
    let text = '';
    if (searchEl) {
      text = searchEl.value.toLowerCase();
    }
    console.log(text);
    let filteredData = [];
    if (view === 'elements' || view === 'players') {
      const searchFields = view === 'players'
        ? ['fName', 'lName', 'info']
        : ['name', 'id', 'description'];
      filteredData = Object.values(dataToDisplay)
        .filter((fData) => {
          let match = true;
          const searchValues = searchFields.map(field => fData[field]);
          if (view === 'players') {
            searchValues.push(`${fData.fName ?? ''} ${fData.lName ?? ''}`);
          }
          if (
            text &&
            !searchValues.some(value =>
              value &&
              String(value).toLowerCase().includes(text)
            )
          ) {
            match = false;
          }
          if (filterRef.current.length > 0) {
            const isPrizepack = fData.type === 'item' && fData.opt_itemType === 'prizepack';
            if (
              view === 'elements' &&
              !filterRef.current.includes(fData.type) &&
              !(isPrizepack && filterRef.current.includes('prizepack'))
            ) {
              match = false;
            } else if (view === 'players' &&
              (filterRef.current.includes('teams') && !fData.team) ||
              (filterRef.current.includes('players') && fData.team)
            ) {
              match = false;
            }
          }
          return match;
        })
    }
    if (view === 'players') {
      filteredData = sortPlayers({
        'playerList': filteredData
      });
    } else if (view === 'elements') {
      filteredData = sortElementsNoThe(filteredData);
    } else if (view === 'gameData') {
      filteredData = Object.values(dataToDisplay);
    }
    setFilteredDataToDisplay(filteredData);
  }

  function saveAsTemplateGame(gameData) {
    setConfirmMessage('Are you sure you want to save this game as a template?');
    approveRef.current = () => {
      approveRef.current = null;
      continueSaveAsTemplate();
    }

    async function continueSaveAsTemplate() {
      setFullLoading(true);
      setConfirmMessage(null);
      const res = await UseCloudFunction(
        'createTemplateGame',
        {
          'gameId': gameData.gameId,
          'appId': userProfile.appId,
          'userProfile': userProfile,
        }
      );
      console.log(res);
      if (res.error) {
        setAlertMessage(`Something went wrong: ${res.error}`);
        return;
      }
      setAlertMessage('Game saved as template');
      approveRef.current = () => {
        navigate('/admin/games');
        approveRef.current = null;
        return;
      }
      setFullLoading(false);
    }
  }

  return (
    <div className='admin-game-data'>
      {
        (dataToDisplay && view !== 'gameData') &&
        <SearchBar search={search} idLabel='item-search' />
      }
      {
        (sortButtons.length > 0) &&
        <div className='sort-buttons'>

          {
            sortButtons
            .sort()
            .map((b, index) => {
              return (
                <button
                  key={b}
                  className={`g-button small-btn ${typeFilter.includes(b) ? 'primary' : ''}`}
                  onClick={() => filterByType(b)} >
                  {b}
                </button>
              )
            })
          }
        </div>
      }
      {
        (view !== 'gameData') &&
        <>
          <div className='g-space-0-5'></div>
          <div className='meta'>Count: {filteredDataToDisplay.length}</div>
          <hr />
          <div className="g-space-0-5"></div>
        </>
      }

      {
        (loading)
        &&
        <div className='center-col'>
          <ThreeDotsLoader />
        </div>
      }
      {
        (
          !loading &&
          filteredDataToDisplay.length > 0 &&
          displayLimit
        )
          ?
          filteredDataToDisplay
            // .splice(0, displayLimit)
            // .sort()
            .map((primData, index) => {
              if (index >= displayLimit) return;
              // let primData = filteredDataToDisplay[d];
              return (
                <div key={index} className='data-arr-div'>
                  {
                    (view === 'gameData' && primData.gameName) &&
                    <div className='design-header'>
                      <ImageContainer
                        src={primData.header}
                        alt={primData.name}
                        className='header'
                        preview={true} />
                      <div className='design-div meta'>
                        <div className='game-color' style={{ 'backgroundColor': primData.color }}></div>
                        Button Color
                      </div>
                      <div className='design-div meta'>
                        {
                          (primData.bg.type === 'image')
                            ?
                            <ImageContainer
                              src={primData.bg.url}
                              className='game-bg'
                              preview={true} />
                            :
                            <div
                              className='game-bg'
                              style={{ 'backgroundImage': `linear-gradient(to top, ${primData.bg.hexBottom}, ${primData.bg.hexTop})` }} >
                            </div>
                        }
                        Background
                      </div>
                      <div className='vert-buttons'>
                        <button
                          className='g-button small-btn'
                          onClick={() => saveAsTemplateGame(primData)}>
                          <FontAwesomeIcon icon={faClone} />
                          Save as Template
                        </button>
                        <button
                          className='g-button small-btn'
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          Delete Game
                        </button>
                      </div>
                    </div>
                  }
                  {
                    (view === 'gameData')
                    &&
                    <h2>{primData.gameName}</h2>
                  }
                  {
                    (view === 'elements') &&
                    <div className={`player-element-div ${primData.status}`}>
                      <div className='element-icon'>
                        <ImageContainer
                          src={primData.icon}
                          preview={true}
                          className='element-icon-img' />
                      </div>
                      <div>
                        <h2>{primData.name}</h2>
                        {primData.type && properCase(primData.type)}
                        {primData.type === 'item' && <span> - {properCase(primData.opt_itemType)}</span>}
                      </div>
                      <div className='buttons'>  
                        <button
                          className='g-button med-btn'
                          onClick={() => navigate(`element/${primData.id}`)}>
                          <FontAwesomeIcon icon={faMagnifyingGlass} />
                        </button>
                      </div>
                    </div>
                  }

                  {
                    (view === 'players') &&
                    <div className='player-element-div'>
                      <div className='element-icon'>
                        <UserIcon profile={primData} noHover={true} />
                      </div>
                      <div className='name'>
                        <h2>{primData.fName} {primData.lName ?? ''}</h2>
                        <div>{primData.team ? 'Team' : 'Player'}</div>
                      </div>
                      <button
                        className='g-button med-btn'
                        onClick={() => navigate(`player/${primData.playerId}`)}>
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                      </button>
                    </div>
                  }
                  <hr />
                  {
                    (view === 'gameData') &&
                    <div className='game-data-div'>
                      <pre>{
                        Object.keys(primData)
                          .sort()
                          .map(d => {
                            if (typeof primData[d] === 'object') {
                              return (
                                <div key={`gamedata-${d}`} className='gamedata-element'>
                                  <span><b>{d.toUpperCase()}:</b></span><br />
                                  <span> {JSON.stringify(primData[d], null, 2)}</span>
                                </div>
                              );
                            } else {
                              return (
                                <div key={`gamedata-${d}`} className='gamedata-element'>
                                  <span><b>{d.toUpperCase()}:</b></span><br />
                                  <span> {primData[d] === true ? 'True' : primData[d] === false ? 'False' : primData[d]}</span>
                                </div>
                              );
                            }
                          })
                      }</pre>
                    </div>
                  }
                </div>
              )
            })
          :
          <div className='center-col'>
            No Data...
          </div>
      }
      {
        (
          filteredDataToDisplay.length > 0 &&
          displayLimit &&
          filteredDataToDisplay.length > displayLimit
        ) &&
        <div className='center'>
          <button
            className='g-button med-btn'
            onClick={() => setDisplayLimit(displayLimit + 20)}
          >
            Load More...
          </button>
        </div>
      }
      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
        onApprove={approveRef.current} />
      <ModalConfirm
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
      <FullPageLoader
        show={fullLoading} />
    </div>
  )
}

export default AdminDataView